import React from 'react';

import { Spinner, BrandButton } from '@zapier/design-system';

import AccountActions from '../components/UniversalTopbar/AccountActions';
import { AccountsMenuContainer } from '../components/AccountsMenu';
import type { UniversalTopbarProps } from '../components/UniversalTopbar';
import { UniversalNavContext } from '../hooks/useUniversalNavContext';

type AccountMenuRendererProps = {
  canTopbarFitBackToAppButton: boolean;
  currentAccountId?: number;
  currentUrl: string;
  isLoadingLoggedIn: boolean;
  isLoggedIn: boolean;
  loginHref: string;
  signupHref: string;
};

type ShouldRender = (accountMenuProps: AccountMenuRendererProps) => boolean;

// This is a function that *makes* a render function. The reason for
// that is so the render function has access to `AccountMenuRendererProps`
// which is necessary in some circumstances.
type MakeRenderAccountMenu = (
  accountMenuProps: AccountMenuRendererProps
) => (props: UniversalTopbarProps) => React.ReactNode;

type RendererConfig = {
  shouldRender: ShouldRender;
  makeRenderer: MakeRenderAccountMenu;
};

const renderAccountsMenuContainerConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return renderProps.isLoggedIn;
  },
  makeRenderer: (renderProps: AccountMenuRendererProps) => (
    props: UniversalTopbarProps
  ) => {
    const backToAppButton = !renderProps.currentUrl.includes('/app/') &&
      renderProps.canTopbarFitBackToAppButton && (
        <UniversalNavContext.Consumer>
          {(context) => (
            <BrandButton
              href={context.getFullyQualifiedURL('/app/zaps')}
              id="back-to-app"
            >
              My Zaps
            </BrandButton>
          )}
        </UniversalNavContext.Consumer>
      );

    return (
      <React.Fragment>
        {backToAppButton}
        <AccountsMenuContainer
          currentAccountId={renderProps.currentAccountId}
          size={props.size}
        />
      </React.Fragment>
    );
  },
};

const renderLoadingConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return !renderProps.isLoggedIn && renderProps.isLoadingLoggedIn;
  },
  makeRenderer: () => () => {
    return (
      <div data-testid="accounts-loader">
        <Spinner color="black" />
      </div>
    );
  },
};

const renderSignUpOnlyConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return renderProps.currentUrl.includes('/login');
  },
  makeRenderer: (renderProps: AccountMenuRendererProps) => (
    props: UniversalTopbarProps
  ) => {
    const isOnReauthenticate = renderProps.currentUrl.includes(
      '/reauthenticate'
    );
    return (
      <AccountActions
        selectedHref={renderProps.currentUrl}
        shouldRenderSignIn={false}
        shouldRenderSignUp={!isOnReauthenticate}
        signupHref={renderProps.signupHref}
        size={props.size}
      />
    );
  },
};

const renderSignInOnlyConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return renderProps.currentUrl.includes('/sign-up');
  },
  makeRenderer: (renderProps: AccountMenuRendererProps) => (
    props: UniversalTopbarProps
  ) => {
    return (
      <AccountActions
        loginHref={renderProps.loginHref}
        selectedHref={renderProps.currentUrl}
        shouldRenderSignIn={true}
        shouldRenderSignUp={false}
        size={props.size}
      />
    );
  },
};

const renderOnboardingConfig: RendererConfig = {
  shouldRender: (renderProps: AccountMenuRendererProps) => {
    return renderProps.currentUrl.includes('/onboarding');
  },
  makeRenderer: () => () => {
    return null;
  },
};

// Order-sensitive array of configurations to render the account menu.
// The first one whose `shouldRender` returns `true` is used.
const configs: Array<RendererConfig> = [
  renderSignUpOnlyConfig,
  renderSignInOnlyConfig,
  renderOnboardingConfig,
  renderAccountsMenuContainerConfig,
  renderLoadingConfig,
];

// This function helps figure out what to render for the accounts menu.
// There are numerous conditions for rendering the accounts menu, and
// instead of passing all of those renderProps into `AccountsMenu` and making
// that component complex and have access to renderProps that it probably
// shouldn't, handle all of that complexity in render functions defined
// within this file.
const findAccountsMenuRenderer = (renderProps: AccountMenuRendererProps) => {
  const foundConfig = configs.find((config: RendererConfig) => {
    return config.shouldRender(renderProps);
  });
  return foundConfig && foundConfig.makeRenderer(renderProps);
};

export default findAccountsMenuRenderer;
